import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../../utils/config";
import { mapToPresentable, mapejos } from "../../utils/Mapejos";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { formatAddress, formatNumero, formatDate } from "../../utils/Utilitats";
import AjudaPressupost from "./AjudaPressupost";
import "../dashboard_v2.css";

/**
 * mode: "nou" | "consultar" | "modificar" | "esborrar"
 * pressupost: objecte que conté les dades del pressupost (si n'hi ha)
 * onSave: funció callback quan es desa (crear o modificar)
 * onCancel: funció callback quan es cancel·la o es tanca
 */
function Detallspressupost({
  mode,
  pressupost,
  pressupostos,
  subnav,
  onNavigate,
  onCanviarMode,
  onSave,
  onCancel,
}) {
  const isReadOnly = mode === "consultar" || mode === "esborrar";
  TeclaEscEnter(onCancel, null);

  const { empresaSeleccionada, usuariActiu, rolUsuari } = useUserContext();
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");
  const pucVersions = tincPermis(rolUsuari, "ACCES");
  const [mostraAjuda, setMostraAjuda] = useState(false);
  const [menuAccions, setMenuAccions] = useState(false);

  // =================== ESTATS / PART 1 (Informació) ===================
  const [idPresIntern, setIdPresIntern] = useState("");
  const [estatGlobal, setEstatGlobal] = useState("");
  const [versioAcceptada, setVersioAcceptada] = useState(0);
  const [numVersions, setNumVersions] = useState(0);
  const [preuMaterials, setPreuMaterials] = useState("");
  const [preuMaObra, setPreuMaObra] = useState("");
  const [preuCost, setPreuCost] = useState("");
  const [preuVenta, setPreuVenta] = useState("");

  // =================== ESTATS / PART 2 ===================
  const [dataAlta, setDataAlta] = useState("");
  const [clientId, setClientId] = useState(null);
  const [clientsOptions, setClientsOptions] = useState([]);
  const [filtreClient, setFiltreClient] = useState("");
  const [clientsFiltrats, setClientsFiltrats] = useState([]);
  const [percentatgeBenefici, setPercentatgeBenefici] = useState(25);
  const [preuFinalVenta, setPreuFinalVenta] = useState(0);
  const [preuManual, setPreuManual] = useState(0);
  const [esPreuManual, setEsPreuManual] = useState(false);
  const [intermediariCheck, setIntermediariCheck] = useState(false);
  const [idIntermediari, setIdIntermediari] = useState(null);
  const [intermediarisOptions, setIntermediarisOptions] = useState([]);
  const [percentatgeInter, setPercentatgeInter] = useState(0);
  const [comentarisInter, setComentarisInter] = useState("");

  // =================== ESTATS / PART 3 (Adreça) ===================
  const [tipusVia, setTipusVia] = useState("");
  const [nomVia, setNomVia] = useState("");
  const [numVia, setNumVia] = useState("");
  const [escalaVia, setEscalaVia] = useState("");
  const [pis, setPis] = useState("");
  const [porta, setPorta] = useState("");
  const [poblacio, setPoblacio] = useState("");
  const [provincia, setProvincia] = useState("");
  const [pais, setPais] = useState("");
  const [codiPostal, setCodiPostal] = useState("");
  const [referencies, setReferencies] = useState("");

  const [tipusViaDefault, setTipusViaDefault] = useState("");
  const [nomViaDefault, setNomViaDefault] = useState("");
  const [numViaDefault, setNumViaDefault] = useState("");
  const [escalaViaDefault, setEscalaViaDefault] = useState("");
  const [pisDefault, setPisDefault] = useState("");
  const [portaDefault, setPortaDefault] = useState("");
  const [poblacioDefault, setPoblacioDefault] = useState("");
  const [provinciaDefault, setProvinciaDefault] = useState("");
  const [paisDefault, setPaisDefault] = useState("");
  const [codiPostalDefault, setCodiPostalDefault] = useState("");
  const [referenciesDefault, setReferenciesDefault] = useState("");

  // =================== ESTATS / PART 4 (Comentaris Pressupost) ===================
  const [comentarisPressupost, setComentarisPressupost] = useState("");

  // =================== ESTATS / PART 5 (Contactes de Client) ===================
  const [contactesClient, setContactesClient] = useState([]);
  const [contacteSeleccionat, setContacteSeleccionat] = useState(null);

  // =================== NAV ENTRE PRESSUPOSTOS ===================
  const currentIndex = Array.isArray(pressupostos)
    ? pressupostos.findIndex((p) => p.idPressupostos === pressupost?.idPressupostos)
    : -1;

  const anteriorPressupost = () => {
    if (!Array.isArray(pressupostos) || pressupostos.length === 0 || currentIndex < 0) return;
    const newIndex = currentIndex === 0 ? pressupostos.length - 1 : currentIndex - 1;
    onNavigate?.(pressupostos[newIndex]);
  };

  const seguentPressupost = () => {
    if (!Array.isArray(pressupostos) || pressupostos.length === 0 || currentIndex < 0) return;
    const newIndex = currentIndex === pressupostos.length - 1 ? 0 : currentIndex + 1;
    onNavigate?.(pressupostos[newIndex]);
  };

  const [adrecaModificada, setAdrecaModificada] = useState(false);

  const inicialitzarAdrecaClient = (client) => {
    if (!client || adrecaModificada) return;
    setTipusViaDefault(client.TipusVia || "");
    setNomViaDefault(client.NomVia || "");
    setNumViaDefault(client.NumVia || "");
    setEscalaViaDefault(client.EscalaVia || "");
    setPisDefault(client.Pis || "");
    setPortaDefault(client.Porta || "");
    setPoblacioDefault(client.Poblacio || "");
    setProvinciaDefault(client.Provincia || "");
    setPaisDefault(client.Pais || "");
    setCodiPostalDefault(client.CodiPostal || "");
    setReferenciesDefault(client.Referencies || "");
  };

  useEffect(() => {
    const client = clientsOptions.find((c) => c.idClients === clientId);
    inicialitzarAdrecaClient(client);
  }, [clientId, clientsOptions]);

  useEffect(() => {
    const fetchContactesClient = async () => {
      if (!clientId) {
        setContactesClient([]);
        setContacteSeleccionat(null);
        return;
      }

      try {
        const resp = await fetch(`${API_URL}/Contactesclient.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idClients: clientId }),
        });
        const data = await resp.json();
        if (data.success) {
          setContactesClient(data.contactes || []);
          if (pressupost?.Contactes_idContactes) {
            setContacteSeleccionat(pressupost.Contactes_idContactes);
          } else {
            setContacteSeleccionat(null);
          }
        } else {
          console.error("Error carregant contactes:", data.message);
          setContactesClient([]);
          setContacteSeleccionat(null);
        }
        setContacteSeleccionat(null);
      } catch (err) {
        console.error("Error al fetchContactesClient:", err);
        setContactesClient([]);
        setContacteSeleccionat(null);
      }
    };

    fetchContactesClient();
  }, [clientId, pressupost]);

  useEffect(() => {
    if (mode !== "nou" && pressupost?.Contactes_idContactes && contactesClient.length > 0) {
      const existeixContacte = contactesClient.some(
        (c) => c.idContacteClients === pressupost.Contactes_idContactes
      );
      if (existeixContacte) {
        setContacteSeleccionat(pressupost.Contactes_idContactes);
      } else {
        setContacteSeleccionat(null);
      }
    }
  }, [contactesClient, pressupost, mode]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const resp = await fetch(`${API_URL}/Clientsempresaselect.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada?.IdEmpresa || 0 }),
        });
        const data = await resp.json();
        if (data.success) {
          setClientsOptions(data.clients || []);
        } else {
          console.error("Error carregant clients:", data.message);
        }
      } catch (err) {
        console.error("Error al fetchClients:", err);
      }
    };
    fetchClients();
  }, [empresaSeleccionada]);

  useEffect(() => {
    if (!filtreClient.trim()) {
      setClientsFiltrats(clientsOptions);
      return;
    }
    const textBusca = filtreClient.toLowerCase().trim();
    const coincidencies = clientsOptions.filter((cli) => {
      const camps = [
        cli.NomClient,
        cli.Nom,
        cli.Cognom1,
        cli.Cognom2,
        cli.NomComercial,
        cli.CIF_DNI,
        cli.TelefonMobil,
        cli.TelefonFixe,
        cli.Email1,
      ];
      return camps.some((v) => (v || "").toLowerCase().includes(textBusca));
    });
    if (clientId && !coincidencies.some((c) => Number(c.idClients) === Number(clientId))) {
      const seleccionat = clientsOptions.find((c) => Number(c.idClients) === Number(clientId));
      setClientsFiltrats(seleccionat ? [seleccionat, ...coincidencies] : coincidencies);
      return;
    }
    setClientsFiltrats(coincidencies);
  }, [filtreClient, clientsOptions, clientId]);

  useEffect(() => {
    const fetchIntermediaris = async () => {
      try {
        const resp = await fetch(`${API_URL}/Intermediaris.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada?.IdEmpresa || 0 }),
        });
        const data = await resp.json();
        if (data.success) {
          setIntermediarisOptions(data.intermediaris || []);
        } else {
          console.error("Error carregant intermediaris:", data.message);
        }
      } catch (err) {
        console.error("Error al fetchIntermediaris:", err);
      }
    };

    if (intermediariCheck) {
      fetchIntermediaris();
    }
  }, [empresaSeleccionada, intermediariCheck]);

  useEffect(() => {
    if (!pressupost || mode === "nou") {
      resetForm();
      return;
    }

    setIdPresIntern(pressupost.idPresIntern || "");
    setEstatGlobal(pressupost.EstatGlobal || "");
    setVersioAcceptada(pressupost.VersioAcceptada || 0);
    setNumVersions(Number(pressupost.NumVersions) || 0);
    setPreuMaterials(pressupost.PreuMaterials || "");
    setPreuMaObra(pressupost.PreuMaObra || "");
    setPreuCost(pressupost.PreuCost || "");
    setPreuVenta(pressupost.PreuFinalVenta || "");

    setDataAlta(pressupost.DataAlta || "");
    setClientId(pressupost.Clients_idClients || null);
    setContacteSeleccionat(pressupost.Contactes_idContactes || null);
    setPercentatgeBenefici(pressupost.PercentatgeBenefici || 25);
    setPreuFinalVenta(pressupost.PreuFinalVenta || 0);
    setPreuManual(pressupost.PreuManual || 0);
    setEsPreuManual(pressupost.EsPreuManual || false);
    setIntermediariCheck(!!pressupost.Intermediari);
    setIdIntermediari(pressupost.IntermediariEntitat || null);
    setPercentatgeInter(pressupost.PercentatgeInter || 0);
    setComentarisInter(pressupost.ComentarisInter || "");

    setTipusVia(pressupost.TipusVia || "");
    setNomVia(pressupost.NomVia || "");
    setNumVia(pressupost.NumVia || "");
    setEscalaVia(pressupost.EscalaVia || "");
    setPis(pressupost.Pis || "");
    setPorta(pressupost.Porta || "");
    setPoblacio(pressupost.Poblacio || "");
    setProvincia(pressupost.Provincia || "");
    setPais(pressupost.Pais || "");
    setCodiPostal(pressupost.CodiPostal || "");
    setReferencies(pressupost.Referencies || "");

    setComentarisPressupost(pressupost.Comentaris || "");
  }, [pressupost, mode]);

  const resetForm = useCallback(() => {
    setIdPresIntern("");
    setEstatGlobal("");
    setVersioAcceptada(0);
    setNumVersions(0);
    setPreuMaterials("");
    setPreuMaObra("");
    setPreuCost("");
    setPreuVenta("");

    const avui = new Date().toISOString().split("T")[0];
    setDataAlta(avui);
    setClientId(null);
    setContactesClient([]);
    setContacteSeleccionat(null);
    setPercentatgeBenefici(25);
    setPreuFinalVenta(0);
    setPreuManual(0);
    setEsPreuManual(false);
    setIntermediariCheck(false);
    setIdIntermediari(null);
    setPercentatgeInter(0);
    setComentarisInter("");

    setTipusVia("");
    setNomVia("");
    setNumVia("");
    setEscalaVia("");
    setPis("");
    setPorta("");
    setPoblacio("");
    setProvincia("");
    setPais("");
    setCodiPostal("");
    setReferencies("");

    setComentarisPressupost("");
  }, []);

  const validarFormulari = () => {
    const errors = [];
    if (!dataAlta) {
      errors.push("El camp 'Data d'alta' és obligatori.");
    }
    if (!clientId) {
      errors.push("Has de seleccionar un 'Client'.");
    }
    if (intermediariCheck && !idIntermediari) {
      errors.push("Has de seleccionar un 'Intermediari'.");
    }
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validarFormulari();
    if (errors.length > 0) {
      alert("Errors trobats:\n" + errors.join("\n"));
      return;
    }

    const pressupostData = {
      idEmpresa: empresaSeleccionada?.IdEmpresa || 0,
      idPressupostos: pressupost?.idPressupostos || null,
      DataAlta: dataAlta,
      Clients_idClients: clientId,
      Contactes_idContactes: contacteSeleccionat || null,
      PercentatgeBenefici: percentatgeBenefici,
      PreuFinalVenta: preuFinalVenta,
      PreuManual: preuManual,
      EsPreuManual: esPreuManual ? 1 : 0,
      Intermediari: intermediariCheck ? 1 : 0,
      IntermediariEntitat: idIntermediari,
      PercentatgeInter: percentatgeInter,
      ComentarisInter: comentarisInter,
      TipusVia: tipusVia || tipusViaDefault,
      NomVia: nomVia || nomViaDefault,
      NumVia: numVia || numViaDefault,
      EscalaVia: escalaVia || escalaViaDefault,
      Pis: pis || pisDefault,
      Porta: porta || portaDefault,
      Poblacio: poblacio || poblacioDefault,
      Provincia: provincia || provinciaDefault,
      Pais: pais || paisDefault,
      CodiPostal: codiPostal || codiPostalDefault,
      Referencies: referencies || referenciesDefault,
      Comentaris: comentarisPressupost,
      Usuari: usuariActiu,
    };

    try {
      if (mode === "nou") {
        const resp = await fetch(`${API_URL}/Noupressupost.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(pressupostData),
        });
        const data = await resp.json();
        if (data.success) {
          alert("Pressupost creat correctament!");
          resetForm();
          //onSave?.(data.newPressupost);
        } else {
          alert("Error en crear el pressupost: " + data.message);
        }
      } else if (mode === "modificar") {
        const resp = await fetch(`${API_URL}/Modificarpressupost.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(pressupostData),
        });
        const data = await resp.json();
        if (data.success) {
          alert("Pressupost modificat correctament!");
          onSave?.({ ...pressupost, ...pressupostData });
        } else {
          alert("Error en modificar el pressupost: " + data.message);
        }
      } else if (mode === "esborrar") {
        const confirmar = window.confirm("Estàs segur que vols esborrar aquest pressupost?");
        if (!confirmar) return;

        const resp = await fetch(`${API_URL}/Esborrarpressupost.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idPressupostos: pressupost.idPressupostos }),
        });
        const data = await resp.json();
        if (data.success) {
          alert("Pressupost esborrat correctament!");
          onCancel();
        } else {
          alert("Error en esborrar el pressupost: " + data.message);
        }
      }
    } catch (error) {
      console.error("Error en handleSubmit Pressupost:", error);
      alert("S'ha produït un error en el procés.");
    }
  };

  const recalcularPreuFinal = () => {
    setEsPreuManual(false);
    const nouPreu = Number(preuCost) * (1 + Number(percentatgeBenefici) / 100);
    setPreuFinalVenta(nouPreu.toFixed(2));
  };

  const formatClientName = (id) => {
    if (!id) return "";
    const found = clientsOptions.find((c) => c.idClients === id);
    return found ? found.NomClient : "";
  };

  const formatIntermediariName = (id) => {
    if (!id) return "";
    const found = intermediarisOptions.find((i) => i.idEntitats === id);
    return found ? found.NomEntitat : "";
  };

  const camp = (label, required, content, mida) => {
    const extra =
      mida === "ample"
        ? "dv2-field--ample"
        : mida === "llarg"
        ? "dv2-field--llarg"
        : mida === "curt"
        ? "dv2-field--curt"
        : "dv2-field--mig";
    return (
      <label className={`dv2-field ${extra}`}>
        <span className={`dv2-label ${required && !isReadOnly ? "dv2-required" : ""}`}>{label}</span>
        {content}
      </label>
    );
  };

  const valor = (txt) => <span className="dv2-value">{txt || "—"}</span>;

  const formatPreu = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return `${formatNumero(n)} €`;
  };

  const nCard = (nou, altres) => (mode === "nou" ? nou : altres);

  const inputAdreca = (valorCamp, defecte, setValor, setDefecte, maxLength) => {
    const esTrans = valorCamp === "" && !!defecte;
    return (
      <input
        type="text"
        className={`dv2-input${esTrans ? " is-placeholder" : ""}`}
        value={valorCamp || defecte}
        maxLength={maxLength}
        onChange={(e) => {
          const v = e.target.value;
          if (valorCamp === "" && defecte) {
            setValor(v.replace(defecte, ""));
            setDefecte("");
            setAdrecaModificada(true);
          } else {
            setValor(v);
          }
        }}
        onClick={(e) => {
          if (valorCamp === "" && defecte) e.target.setSelectionRange(0, 0);
        }}
      />
    );
  };

  const selectAdrecaTipus = () => {
    const esTrans = tipusVia === "" && !!tipusViaDefault;
    return (
      <select
        className={`dv2-select${esTrans ? " is-placeholder" : ""}`}
        value={tipusVia || tipusViaDefault}
        onChange={(e) => {
          const v = e.target.value;
          if (tipusVia === "" && tipusViaDefault !== "") {
            setTipusVia(v);
            setTipusViaDefault("");
            setAdrecaModificada(true);
          } else {
            setTipusVia(v);
          }
        }}
      >
        <option value="">Selecciona un tipus de via</option>
        {Object.keys(mapejos.tipusVia).map((option) => (
          <option key={option} value={option}>
            {mapToPresentable(option, mapejos.tipusVia)}
          </option>
        ))}
      </select>
    );
  };

  const clientActiu = clientsOptions.find((c) => Number(c.idClients) === Number(clientId));
  const textContacte = pressupost?.Contactes_idContactes
    ? `${pressupost.NomContacte || ""}  ${pressupost.CognomContacte || ""} ${
        pressupost.TelefonContacte ? "- " + pressupost.TelefonContacte : ""
      } ${pressupost.EmailContacte ? "- " + pressupost.EmailContacte : ""}`
    : [clientActiu?.TelefonMobil || clientActiu?.TelefonFixe, clientActiu?.Email1]
        .map((v) => (v || "").trim())
        .filter(Boolean)
        .join(" · ") || "—";

  const renderCamps = () => (
    <>
      {mode !== "nou" && (
        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">1</span>
            <h2 className="dv2-card-title">Identificació</h2>
          </div>
          <div className="dv2-fields dv2-fields--sizes">
            {camp("Nº de pressupost", false, valor(idPresIntern))}
            {camp("Estat global", false, valor(estatGlobal))}
            {Number(versioAcceptada) > 0
              ? camp("Versió acceptada", false, valor(versioAcceptada))
              : camp("Nº de versions", false, valor(numVersions))}
            <span className="dv2-fields-break" aria-hidden="true" />
            {camp("Preu materials", false, valor(formatPreu(preuMaterials)))}
            {camp("Preu mà d'obra", false, valor(formatPreu(preuMaObra)))}
            {camp("Preu de cost", false, valor(formatPreu(preuCost)))}
            {camp("Preu de venda", false, valor(formatPreu(preuFinalVenta || preuVenta)))}
          </div>
        </section>
      )}

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">{mode === "nou" ? "1" : "2"}</span>
          <h2 className="dv2-card-title">Client i condicions</h2>
        </div>
        {isReadOnly ? (
          <div className="dv2-fields dv2-fields--sizes">
            {camp("Data d'alta", false, valor(formatDate(dataAlta)), "curt")}
            {camp("Client", false, valor(formatClientName(clientId)), "llarg")}
            {camp("Contacte", false, valor(textContacte), "llarg")}
            <span className="dv2-fields-break" aria-hidden="true" />
            {camp("Percentatge benefici", false, valor(`${percentatgeBenefici}%`), "curt")}
            {camp("Preu final", false, valor(formatPreu(preuFinalVenta)), "curt")}
          </div>
        ) : (
          <div className="dv2-fields dv2-fields--sizes">
            {camp(
              "Data d'alta",
              true,
              <input className="dv2-input" type="date" value={dataAlta} onChange={(e) => setDataAlta(e.target.value)} />,
              "curt"
            )}
            {camp(
              "Cerca client",
              false,
              <input
                type="text"
                className="dv2-input"
                value={filtreClient}
                onChange={(e) => setFiltreClient(e.target.value)}
                placeholder="Nom, cognoms, nom comercial…"
              />,
              "llarg"
            )}
            <span className="dv2-fields-break" aria-hidden="true" />
            {camp(
              "Client",
              true,
              <select
                className="dv2-select"
                value={clientId || ""}
                onChange={(e) => setClientId(Number(e.target.value) || null)}
              >
                <option value="">— Selecciona un client —</option>
                {clientsFiltrats.map((cli) => (
                  <option key={cli.idClients} value={cli.idClients}>
                    {cli.NomClient}
                  </option>
                ))}
              </select>,
              "llarg"
            )}
            {contactesClient.length > 0 &&
              camp(
                "Contacte del client",
                false,
                <select
                  className="dv2-select"
                  value={contacteSeleccionat || ""}
                  onChange={(e) => setContacteSeleccionat(Number(e.target.value) || null)}
                >
                  <option value="">— Selecciona un contacte (opcional) —</option>
                  {contactesClient.map((contacte) => (
                    <option key={contacte.idContacteClients} value={contacte.idContacteClients}>
                      {contacte.Nom} {contacte.Cognom1}
                    </option>
                  ))}
                </select>,
                "llarg"
              )}
            {camp(
              "Percentatge benefici",
              true,
              <input
                className="dv2-input"
                type="number"
                step="0.01"
                value={percentatgeBenefici}
                onChange={(e) => setPercentatgeBenefici(e.target.value)}
              />,
              "curt"
            )}
            {mode === "modificar" && (
              <>
                <span className="dv2-fields-break" aria-hidden="true" />
                {camp(
                  "Preu final",
                  false,
                  <div className="dv2-controlRow">
                    <input
                      className="dv2-input"
                      type="number"
                      step="0.01"
                      value={preuFinalVenta}
                      onChange={(e) => {
                        setPreuFinalVenta(e.target.value);
                        setPreuManual(e.target.value);
                        setEsPreuManual(true);
                      }}
                    />
                    <button type="button" className="dv2-btn dv2-btn--compact" onClick={recalcularPreuFinal} title="Recalcular">
                      ♻
                    </button>
                  </div>,
                  "curt"
                )}
              </>
            )}
          </div>
        )}
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">{nCard("2", "3")}</span>
          <h2 className="dv2-card-title">Descripció</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          {camp(
            "Descripció del pressupost",
            false,
            isReadOnly ? (
              valor(comentarisPressupost || "No hi ha descripció.")
            ) : (
              <textarea
                className="dv2-textarea"
                value={comentarisPressupost}
                onChange={(e) => setComentarisPressupost(e.target.value)}
                maxLength="256"
                rows={3}
              />
            ),
            "ample"
          )}
        </div>
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">{nCard("3", "4")}</span>
          <h2 className="dv2-card-title">Intermediari</h2>
        </div>
        {isReadOnly ? (
          <div className="dv2-fields dv2-fields--sizes">
            {camp("Intermediari", false, valor(intermediariCheck ? "Sí" : "No"))}
            {intermediariCheck && (
              <>
                {camp("Entitat", false, valor(formatIntermediariName(idIntermediari)), "llarg")}
                {camp("Percentatge", false, valor(percentatgeInter))}
                {camp("Benefici", false, valor(formatPreu(pressupost?.BeneficiInter)))}
                {camp("Preu final intermediari", false, valor(formatPreu(pressupost?.PreuFinalVentaInter)))}
                {camp("Comentaris", false, valor(comentarisInter), "ample")}
              </>
            )}
          </div>
        ) : (
          <div className="dv2-fields dv2-fields--sizes">
            <label className="dv2-toggleRow">
              <span className="dv2-toggle">
                <input
                  type="checkbox"
                  checked={intermediariCheck}
                  onChange={(e) => setIntermediariCheck(e.target.checked)}
                />
                <span className="dv2-toggle-track" />
              </span>
              <span>Intermediari</span>
            </label>
            {intermediariCheck && (
              <>
                {camp(
                  "Intermediari",
                  true,
                  <select
                    className="dv2-select"
                    value={idIntermediari || ""}
                    onChange={(e) => setIdIntermediari(Number(e.target.value) || null)}
                  >
                    <option value="">— Selecciona un intermediari —</option>
                    {intermediarisOptions.map((itm) => (
                      <option key={itm.idEntitats} value={itm.idEntitats}>
                        {itm.NomEntitat}
                      </option>
                    ))}
                  </select>,
                  "llarg"
                )}
                {camp(
                  "Percentatge",
                  false,
                  <input
                    className="dv2-input"
                    type="number"
                    step="0.01"
                    value={percentatgeInter}
                    onChange={(e) => setPercentatgeInter(e.target.value)}
                  />,
                  "curt"
                )}
                {camp(
                  "Comentaris intermediari",
                  false,
                  <textarea
                    className="dv2-textarea"
                    value={comentarisInter}
                    onChange={(e) => setComentarisInter(e.target.value)}
                    rows={3}
                  />,
                  "ample"
                )}
              </>
            )}
          </div>
        )}
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">{nCard("4", "5")}</span>
          <h2 className="dv2-card-title">Adreça</h2>
        </div>
        {isReadOnly ? (
          <div className="dv2-fields dv2-fields--sizes">
            {camp(
              "Adreça",
              false,
              valor(
                formatAddress({
                  TipusVia: tipusVia,
                  NomVia: nomVia,
                  NumVia: numVia,
                  EscalaVia: escalaVia,
                  Pis: pis,
                  Porta: porta,
                  Poblacio: poblacio,
                  CodiPostal: codiPostal,
                })
              ),
              "ample"
            )}
            {camp("Referències", false, valor(referencies || "Sense referències."), "ample")}
          </div>
        ) : (
          <div className="dv2-fields dv2-fields--sizes">
            {camp("Tipus de via", true, selectAdrecaTipus())}
            {camp("Nom de la via", true, inputAdreca(nomVia, nomViaDefault, setNomVia, setNomViaDefault, 100), "llarg")}
            {camp("Número", true, inputAdreca(numVia, numViaDefault, setNumVia, setNumViaDefault, 15), "curt")}
            {camp("Escala", false, inputAdreca(escalaVia, escalaViaDefault, setEscalaVia, setEscalaViaDefault, 10), "curt")}
            {camp("Pis", false, inputAdreca(pis, pisDefault, setPis, setPisDefault, 25), "curt")}
            {camp("Porta", false, inputAdreca(porta, portaDefault, setPorta, setPortaDefault, 5), "curt")}
            <span className="dv2-fields-break" aria-hidden="true" />
            {camp("Població", true, inputAdreca(poblacio, poblacioDefault, setPoblacio, setPoblacioDefault, 100), "llarg")}
            {camp("Província", true, inputAdreca(provincia, provinciaDefault, setProvincia, setProvinciaDefault, 50))}
            {camp("País", true, inputAdreca(pais, paisDefault, setPais, setPaisDefault, 100))}
            {camp("Codi postal", true, inputAdreca(codiPostal, codiPostalDefault, setCodiPostal, setCodiPostalDefault, 10), "curt")}
            {camp(
              "Referències",
              false,
              <textarea
                className={`dv2-textarea${referencies === "" && referenciesDefault ? " is-placeholder" : ""}`}
                value={referencies || referenciesDefault}
                maxLength="256"
                onChange={(e) => {
                  const v = e.target.value;
                  if (referencies === "" && referenciesDefault !== "") {
                    setReferencies(v.replace(referenciesDefault, ""));
                    setReferenciesDefault("");
                    setAdrecaModificada(true);
                  } else {
                    setReferencies(v);
                  }
                }}
                onClick={(e) => {
                  if (referencies === "" && referenciesDefault) e.target.setSelectionRange(0, 0);
                }}
              />,
              "ample"
            )}
          </div>
        )}
      </section>
    </>
  );

  const titolFitxa =
    mode === "nou"
      ? "Nou pressupost"
      : mode === "esborrar"
      ? "Esborrar pressupost"
      : mode === "modificar"
      ? "Modificar pressupost"
      : "Pressupost";
  const descripcioCapcalera = String(comentarisPressupost || pressupost?.Comentaris || "")
    .replace(/\s+/g, " ")
    .trim();
  const subtitolFitxa = pressupost?.idPresIntern
    ? [pressupost.idPresIntern, pressupost.NomClient, descripcioCapcalera].filter(Boolean).join(" · ")
    : "Fitxa del pressupost";
  const potNavegar = mode === "consultar" && Array.isArray(pressupostos) && pressupostos.length > 1 && currentIndex >= 0;

  return (
    <form className="dv2-entity" onSubmit={(e) => e.preventDefault()}>
      {mostraAjuda && <AjudaPressupost pestanya="dades" onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${mode === "esborrar" ? "is-danger" : ""}`} aria-hidden="true">
            {mode === "esborrar" ? "🗑️" : "📄"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titolFitxa}</h1>
            <p className="dv2-pageHeader-subtitle">{subtitolFitxa}</p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <button
            type="button"
            className="dv2-btn dv2-btn--icon dv2-btn--ghost"
            title="Com funciona la fitxa del Pressupost"
            aria-label="Ajuda"
            onClick={() => setMostraAjuda(true)}
          >
            ?
          </button>
          <button type="button" className="dv2-btn" onClick={onCancel}>
            Tornar
          </button>
          {mode === "consultar" && pucModificar && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => onCanviarMode?.("modificar")}>
              Modificar
            </button>
          )}
          {mode === "consultar" && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  {pucModificar && (
                    <button
                      type="button"
                      className="dv2-menu-item"
                      onClick={() => {
                        setMenuAccions(false);
                        onCanviarMode?.("ofertes");
                      }}
                    >
                      Ofertes
                    </button>
                  )}
                  {pucVersions && (
                    <button
                      type="button"
                      className="dv2-menu-item"
                      onClick={() => {
                        setMenuAccions(false);
                        onCanviarMode?.("versions");
                      }}
                    >
                      Versions
                    </button>
                  )}
                  {pucEsborrar && (
                    <button
                      type="button"
                      className="dv2-menu-item is-danger"
                      onClick={() => {
                        setMenuAccions(false);
                        onCanviarMode?.("esborrar");
                      }}
                    >
                      Esborrar
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {(mode === "nou" || mode === "modificar") && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
              Desar
            </button>
          )}
          {mode === "esborrar" && (
            <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
              Esborrar pressupost
            </button>
          )}
        </div>
      </div>
      {subnav}

      {mode === "esborrar" && (
        <div className="dv2-banner dv2-banner--danger">
          Aquesta acció no es pot desfer. Si el pressupost té versions o relacions, el servidor pot impedir l'esborrat.
        </div>
      )}

      <div className="dv2-entity-body">{renderCamps()}</div>

      {(potNavegar || mode === "nou" || mode === "modificar" || mode === "esborrar") && (
        <div className="dv2-entity-dock">
          {potNavegar && (
            <div className="dv2-recordNav">
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={anteriorPressupost} title="Anterior">
                ‹
              </button>
              <span className="dv2-recordNav-info">
                {currentIndex + 1} / {pressupostos.length}
              </span>
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={seguentPressupost} title="Següent">
                ›
              </button>
            </div>
          )}
          {(mode === "nou" || mode === "modificar" || mode === "esborrar") && (
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" onClick={onCancel}>
                Cancel·lar
              </button>
              {mode === "esborrar" ? (
                <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
                  Esborrar pressupost
                </button>
              ) : (
                <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
                  {mode === "nou" ? "Desar pressupost" : "Desar"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </form>
  );
}

export default Detallspressupost;
